import {
	FormItem,
	Select,
	SelectOption,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import {
	customColSpanValues,
	customRowSpanValues,
	doesAppSupportCustomDimensions,
	getAvailableDimensions,
	isPredefinedDimension
} from "client/Features/Workspace/appTypeHelper";
import { GridDimension } from "client/Api/Workspace/common";
import { generateTrackingId } from "client/Misc/utils";

export type AppDimensionSelectFormItemProps = {
	appType: AppType;
	attachable: IValueInputAttachable<GridDimension>;
};

export const AppDimensionSelectFormItem = (props: AppDimensionSelectFormItemProps): ReactElement => {
	const {
		appType,
		attachable,
	} = props;

	const [isCustomSelected, setIsCustomSelected] = useState<boolean>(false);

	const doesSupportCustom = useMemo(
		(): boolean => doesAppSupportCustomDimensions(appType),
		[appType]
	);

	const dimensionOptions = useMemo(
		(): readonly DimensionOption[] => {
			const availableDimensions: readonly GridDimension[] = getAvailableDimensions(appType);

			const result: DimensionOption[] = availableDimensions
				.map(
					(x): DimensionOption => {
						const { columnSpan, rowSpan } = x;
						return {
							text: `${columnSpan} x ${rowSpan}`,
							value: `${columnSpan}_${rowSpan}`,
							dimension: x
						};
					}
				);

			if (doesSupportCustom) {
				result.push({
					value: customDimension,
					text: localize("_Custom"),
					valueTrackingId: "custom",
					groupName: "custom"
				});
			}

			return result;
		},
		[appType, doesSupportCustom]
	);

	const {
		columnSpan: currentColumnSpan,
		rowSpan: currentRowSpan
	} = attachable.value;

	const value = useMemo(
		(): string | typeof customDimension | undefined => {
			let result: DimensionOption | undefined;
			if (isCustomSelected) {
				result = dimensionOptions.find((x) => x.value === customDimension);
			} else {
				result = dimensionOptions
					.find(
						x => typeof x.value === "string"
							&& x.dimension.columnSpan === currentColumnSpan
							&& x.dimension.rowSpan === currentRowSpan
					);
			}

			return result?.value;
		},
		[
			currentColumnSpan,
			currentRowSpan,
			dimensionOptions,
			isCustomSelected
		]
	);

	useEffect(
		(): void => {
			if (!doesSupportCustom
				|| currentColumnSpan < 2
				|| currentRowSpan < 2
			) {
				setIsCustomSelected(false);
			}
		},
		[
			currentColumnSpan,
			currentRowSpan,
			doesSupportCustom
		]
	);

	useEffect(
		(): void => {
			if (doesSupportCustom
				&& currentColumnSpan >= 2
				&& currentRowSpan >= 2
				&& !isPredefinedDimension(
					appType,
					{
						columnSpan: currentColumnSpan,
						rowSpan: currentRowSpan
					}
				)
			) {
				setIsCustomSelected(true);
			}
		},
		[
			appType,
			currentColumnSpan,
			currentRowSpan,
			doesSupportCustom,
			value
		]
	);

	const applyDimension = useCallback(
		(value: GridDimension): void => {
			attachable.reducer
				.setValue(value);

			attachable.validation
				.triggerInputFinished();
		},
		[attachable.reducer, attachable.validation]
	);

	const dimensionChangeHandler = useCallback(
		(newValue: string | typeof customDimension): void => {
			let newDimension: GridDimension | undefined;

			if (newValue === customDimension) {
				if (currentColumnSpan < 2 || currentRowSpan < 2) {
					newDimension = {
						columnSpan: Math.max(2, currentColumnSpan),
						rowSpan: Math.max(2, currentRowSpan)
					};
				}

				setIsCustomSelected(true);
			} else {
				const option: DimensionOption | undefined = dimensionOptions.find(x => x.value === newValue);

				if (option && typeof option.value === "string") {
					newDimension = option.dimension;
				}

				setIsCustomSelected(false);
			}

			if (newDimension) {
				applyDimension(newDimension);
			}
		},
		[
			applyDimension,
			currentColumnSpan,
			currentRowSpan,
			dimensionOptions
		]
	);

	const widthChangeHandler = useCallback(
		(columnSpan: number): void => {
			applyDimension({
				columnSpan,
				rowSpan: currentRowSpan
			});
		},
		[applyDimension, currentRowSpan]
	);

	const heightChangeHandler = useCallback(
		(rowSpan: number): void => {
			applyDimension({
				columnSpan: currentColumnSpan,
				rowSpan
			});
		},
		[applyDimension, currentColumnSpan]
	);

	return (
		<>
			<FormItem
				label={localize("_Size")}
			>
				<Select
					value={value}
					onChange={dimensionChangeHandler}
					options={dimensionOptions}
					trackingId={attachable.reducer.path}
					menuTitle={localize("_Size")}
				/>
				<ValidationError {...attachable.attachToValidationError()}/>
			</FormItem>

			{isCustomSelected && (
				<>
					<FormItem
						label={localize("_Width")}
					>
						<Select
							value={currentColumnSpan}
							onChange={widthChangeHandler}
							options={customColSpanOptions}
							trackingId={generateTrackingId(attachable.reducer.path, "__width")}
							menuTitle={localize("_Width")}
						/>
						<ValidationError {...attachable.attachToValidationError()} />
					</FormItem>

					<FormItem
						label={localize("_Height")}
					>
						<Select
							value={currentRowSpan}
							onChange={heightChangeHandler}
							options={customRowSpanOptions}
							trackingId={generateTrackingId(attachable.reducer.path, "__height")}
							menuTitle={localize("_Height")}
						/>
						<ValidationError {...attachable.attachToValidationError()} />
					</FormItem>
				</>
			)}
		</>
	);
};

AppDimensionSelectFormItem.displayName = "AppDimensionSelectFormItem";

const customDimension = Symbol("custom dimension");

type DimensionOption = SelectOption<typeof customDimension>
	| SelectOption<string> & {
		dimension: GridDimension;
	};

const customColSpanOptions: readonly SelectOption<number>[] = customColSpanValues
	.map((x): SelectOption<number> => ({
		text: `${x}`,
		value: x
	}));

const customRowSpanOptions: readonly SelectOption<number>[] = customRowSpanValues
	.map((x): SelectOption<number> => ({
		text: `${x}`,
		value: x
	}));
